import { Directive, inject, OnDestroy, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { TabComponent } from '../../../components/tab/tab.component';
import { AuthenticationService } from '../../../services/authentication.service';
import { CustomerService } from '../../../services/customer.service';
import { SnackbarService } from '../../../services/snackbar.service';
import {
    canAcceptCustomerDrafts,
    canManageCustomerDrafts,
} from '../../../utils/customer-draft-roles';
import { ExtraBodyParams, RowAction, TableRow } from '../../../components/table/table.interface';
import { DraftActionType } from '../../../interfaces/apis/customer-drafts';
import { APITableComponent } from '../../../components/table/api-table/api-table.component';
import { Subscription } from 'rxjs';

/** Action type for modal confirmations */
type DraftModalAction = 'remove' | 'approve' | 'reject';

/**
 * Base class for draft tab components.
 * Handles role-based actions: approve/reject for approvers and remove for draft editors.
 */
@Directive()
export abstract class BaseDraftTabComponent extends TabComponent implements OnInit, OnDestroy {
    protected readonly authService = inject(AuthenticationService);
    protected readonly customerService = inject(CustomerService);
    protected readonly snackbar = inject(SnackbarService);

    /** Subscription for cleanup */
    private refreshSubscription?: Subscription;

    /** The endpoint to use for fetching draft table data */
    tableEndpoint = '/customer-drafts/tables';

    /** The action type for this draft tab - must be set by subclasses */
    abstract readonly draftAction: DraftActionType;

    /** Extra body params for the table API - wraps draftAction in generic format */
    get tableExtraBody(): ExtraBodyParams {
        return { action: this.draftAction };
    }

    /** Whether the current role can accept or reject drafts. */
    canAcceptDrafts = false;

    /** Whether the current role can create, edit, and delete customer drafts. */
    canManageDrafts = false;


    /** Reference to the row action template (for representatives - Remove) */
    @ViewChild('rowActionTemplate', { static: true })
    rowActionTemplate!: TemplateRef<any>;

    /** Reference to the approve button template (for supervisors) */
    @ViewChild('approveActionTemplate', { static: true })
    approveActionTemplate!: TemplateRef<any>;

    /** Reference to the reject button template (for supervisors) */
    @ViewChild('rejectActionTemplate', { static: true })
    rejectActionTemplate!: TemplateRef<any>;
    /** Reference to the draft table for refreshing after actions */
    @ViewChild('draftTable')
    draftTable?: APITableComponent;

    /** Confirmation modal state */
    confirmationModal = signal<boolean>(false);

    /** Modal action type */
    modalAction: DraftModalAction = 'remove';

    /** Items to be acted upon */
    itemsToAction: TableRow[] = [];

    /** Action column configuration */
    actionColConfig: RowAction[] = [];

    async ngOnInit(): Promise<void> {
        const userData = await this.authService.getUserData();
        this.canAcceptDrafts = canAcceptCustomerDrafts(userData?.role);
        this.canManageDrafts = canManageCustomerDrafts(userData?.role);

        this.tableEndpoint = this.canAcceptDrafts
            ? '/customer-drafts/tables/supervisor'
            : '/customer-drafts/tables';

        this.setupActionColumns();
        this.setupRefreshSubscription();
    }

    ngOnDestroy(): void {
        this.refreshSubscription?.unsubscribe();
    }

    /**
     * Sets up subscription to listen for table refresh events.
     */
    private setupRefreshSubscription(): void {
        this.refreshSubscription = this.customerService.draftTableRefresh$.subscribe(actionType => {
            // Refresh if it's 'all' or matches this tab's action type
            if (actionType === 'all' || actionType === this.draftAction) {
                this.refreshTable();
            }
        });
    }

    /**
     * Configures action columns based on user role.
     */
    private setupActionColumns(): void {
        if (this.canAcceptDrafts) {
            this.actionColConfig = [
                {
                    actionButton: this.approveActionTemplate,
                    headerName: this.dictionaryService.get('Approve'),
                    onClick: this.handleApprove,
                },
                {
                    actionButton: this.rejectActionTemplate,
                    headerName: this.dictionaryService.get('Reject'),
                    onClick: this.handleReject,
                },
            ];
        } else if (this.canManageDrafts) {
            this.actionColConfig = [
                {
                    actionButton: this.rowActionTemplate,
                    headerName: this.dictionaryService.get('Remove'),
                    onClick: this.handleRemove,
                },
            ];
        } else {
            this.actionColConfig = [];
        }
    }

    /** Opens remove confirmation modal (representatives) */
    handleRemove = (row: TableRow): void => {
        if (!this.canManageDrafts) {
            return;
        }

        this.modalAction = 'remove';
        this.itemsToAction = [row];
        this.confirmationModal.set(true);
    };

    /** Opens approve confirmation modal (supervisors) */
    handleApprove = (row: TableRow): void => {
        if (!this.canAcceptDrafts) {
            return;
        }

        this.modalAction = 'approve';
        this.itemsToAction = [row];
        this.confirmationModal.set(true);
    };

    /** Opens reject confirmation modal (supervisors) */
    handleReject = (row: TableRow): void => {
        if (!this.canAcceptDrafts) {
            return;
        }

        this.modalAction = 'reject';
        this.itemsToAction = [row];
        this.confirmationModal.set(true);
    };

    /** Confirms the action and calls appropriate API */
    confirmAction = (): void => {
        // Extract data before closing modal (which clears itemsToAction)
        const items = [...this.itemsToAction];

        // Close modal first
        this.closeModal();

        // Then execute the action with the extracted items
        switch (this.modalAction) {
            case 'reject':
                this.executeReject(items);
                break;
            case 'approve':
                this.executeApprove(items);
                break;
            default:
                this.executeRemove(items);
        }
    };

    /** Closes the modal and resets state */
    cancelAction = (): void => {
        this.closeModal();
    };

    private closeModal(): void {
        this.confirmationModal.set(false);
        this.itemsToAction = [];
    }

    /**
     * Executes reject API call for supervisors.
     * - 'draftId' -> 'Draft Id'
     * - 'customerDraftId' -> 'Customer Draft Id'
     */
    private executeReject(items: TableRow[]): void {
        // Access using camelCase keys as they come from the table
        const draftIds = items.map(item => item.value['draftId'] as string);
        const customerDraftId = items[0]?.value['customerDraftId'] as string;

        if (!customerDraftId || draftIds.length === 0 || draftIds.some(id => !id)) {
            console.error('Missing draft information - items:', items);
            this.snackbar.showError('Missing draft information');
            return;
        }

        this.customerService.rejectDrafts(customerDraftId, draftIds).subscribe({
            next: (res: any) => {
                this.snackbar.showByCode(res?.messageCode ?? 'S0807001');
                this.refreshTable();
            },
            error: (err) => {
                if (err.error?.messageCode) {
                    this.snackbar.showByCode(err.error.messageCode);
                } else {
                    this.snackbar.showError(err.error?.message || 'Failed to reject drafts');
                }
            },
        });
    }

    /**
     * Executes approve API call for supervisors.
     * Uses the appropriate endpoint based on draftAction type:
     * - Creation: /creation-drafts-approve/{customerDraftId}
     * - Editing: /editing-drafts-approve/{customerDraftId}
     * - Deletion: /deletion-drafts-approve/{customerDraftId}
     */
    private executeApprove(items: TableRow[]): void {
        // Access using camelCase keys as they come from the table
        const draftIds = items.map(item => item.value['draftId'] as string);
        const customerDraftId = items[0]?.value['customerDraftId'] as string;

        if (!customerDraftId || draftIds.length === 0 || draftIds.some(id => !id)) {
            console.error('Missing draft information - items:', items);
            this.snackbar.showError('Missing draft information');
            return;
        }

        const approveSuccessCodeMap: Record<DraftActionType, string> = {
            'Creation': 'S0804001',
            'Editing': 'S0805001',
            'Deletion': 'S0806001',
        };
        this.customerService.approveDrafts(this.draftAction, customerDraftId, draftIds).subscribe({
            next: (res: any) => {
                this.snackbar.showByCode(res?.messageCode ?? approveSuccessCodeMap[this.draftAction]);
                this.refreshTable();
            },
            error: (err) => {
                if (err.error?.messageCode) {
                    this.snackbar.showByCode(err.error.messageCode);
                } else {
                    this.snackbar.showError(err.error?.message || 'Failed to approve drafts');
                }
            },
        });
    }

    /**
     * Executes remove from draft for representatives.
     * DELETE /api-v3/customer-drafts/{customerDraftId}/drafts/{draftId}
     */
    private executeRemove(items: TableRow[]): void {
        // Access using camelCase keys as they come from the table
        const customerDraftId = items[0]?.value['customerDraftId'] as string;
        const draftIds = items.map(item => item.value['draftId'] as string);

        if (!customerDraftId || draftIds.length === 0 || draftIds.some(id => !id)) {
            console.error('Missing draft information - items:', items);
            this.snackbar.showError('Missing draft information');
            return;
        }

        // Call remove API for each draft
        let completedCount = 0;
        let hasError = false;

        draftIds.forEach(draftId => {
            this.customerService.removeDraft(customerDraftId, draftId).subscribe({
                next: () => {
                    completedCount++;
                    if (completedCount === draftIds.length) {
                        if (!hasError) {
                            this.snackbar.showByCode('S0812001');
                            this.refreshTable();
                        }
                    }
                },
                error: (err) => {
                    hasError = true;
                    if (err.error?.messageCode) {
                        this.snackbar.showByCode(err.error.messageCode);
                    } else {
                        this.snackbar.showError(err.error?.message || 'Failed to remove draft');
                    }
                },
            });
        });
    }
    /**
     * Refreshes the table data by calling the table component's refresh method.
     */
    private refreshTable(): void {
        this.draftTable?.refresh();
    }

    /** Modal title getter */
    getModalTitle(): string {
        return this.dictionaryService.get('Confirmation');
    }

    /** Confirmation message getter based on action and item count */
    getConfirmationMessage(): string {
        const isSingle = this.itemsToAction.length === 1;
        const messageKey = this.getMessageKey(isSingle);
        return this.dictionaryService.get(messageKey);
    }

    private getMessageKey(isSingle: boolean): string {
        const actionMap: Record<DraftModalAction, { single: string; multiple: string }> = {
            approve: {
                single: 'ApproveSingleDraftConfirmation',
                multiple: 'ApproveMultipleDraftsConfirmation',
            },
            reject: {
                single: 'RejectSingleDraftConfirmation',
                multiple: 'RejectMultipleDraftsConfirmation',
            },
            remove: {
                single: 'RemoveSingleDraftConfirmation',
                multiple: 'RemoveMultipleDraftsConfirmation',
            },
        };
        return isSingle ? actionMap[this.modalAction].single : actionMap[this.modalAction].multiple;
    }

    /** Accept All modal state (for supervisors) */
    acceptAllModal = signal<boolean>(false);

    /**
     * Whether the current draft table has any rows.
     */
    hasDraftRows(): boolean {
        return (this.draftTable?.tableComponent?.tableContext.tableData().rows.length ?? 0) > 0;
    }

    /**
     * Whether the Accept All button should be visible.
     */
    canShowAcceptAllButton(): boolean {
        return this.canAcceptDrafts && this.hasDraftRows();
    }

    /**
     * Opens Accept All confirmation modal (supervisors only).
     */
    handleAcceptAll = (): void => {
        if (!this.canShowAcceptAllButton()) {
            return;
        }
        this.acceptAllModal.set(true);
    };

    /**
     * Confirms Accept All and calls appropriate API.
     */
    confirmAcceptAll = (): void => {
        if (!this.canAcceptDrafts) {
            return;
        }

        const rows = this.draftTable?.tableComponent?.tableContext.tableData().rows ?? [];
        const draftIds = rows.map(row => row.value['draftId'] as string);
        const customerDraftId = rows[0]?.value['customerDraftId'] as string;

        if (!customerDraftId || draftIds.length === 0 || draftIds.some(id => !id)) {
            console.error('Missing draft information - rows:', rows);
            this.snackbar.showError('Missing draft information');
            return;
        }

        this.acceptAllModal.set(false);

        this.customerService.approveDrafts(this.draftAction, customerDraftId, draftIds).subscribe({
            next: (res: any) => {
                this.snackbar.showByCode(res?.messageCode ?? this.getApproveSuccessCode());
                this.refreshTable();
            },
            error: (err) => {
                if (err.error?.messageCode) {
                    this.snackbar.showByCode(err.error.messageCode);
                } else {
                    this.snackbar.showError(err.error?.message || 'Failed to approve drafts');
                }
            },
        });
    };

    private getApproveSuccessCode(): string {
        const approveSuccessCodeMap: Record<DraftActionType, string> = {
            'Creation': 'S0804001',
            'Editing': 'S0805001',
            'Deletion': 'S0806001',
        };
        return approveSuccessCodeMap[this.draftAction];
    }

    /**
     * Cancels Accept All and closes the modal.
     */
    cancelAcceptAll = (): void => {
        this.acceptAllModal.set(false);
    };

    public async main(): Promise<void> { }
}
